import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { pluralizeWithCount } from "@common/helpers/string";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { format, parseISO } from "date-fns";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useOverlays } from "contexts/OverlayContext";
import {
    undoReconciliationLockBlockersQueryKey,
    useLastBankReconciliationRemovalMutation,
    useUndoReconciliationLockBlockers,
} from "@api/reconciliation.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VIEW_ONLY_WRITE_REFUSAL_MESSAGE } from "@common/userWorkspaceRoles";
import {
    isViewOnlyRefusalError,
    VIEW_ONLY_TOAST_KEY,
} from "@lib/viewOnlyRefusal";
import { isReconciliationPeriodLockedError } from "@helpers/lock";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { ExportMenuItem } from "@design-system/ExportMenuItem/ExportMenuItem";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { ReconciliationLogDrawer } from "@components/auditLog/reconciliation/ReconciliationLogDrawer";
import { getReconcileDisabledReason } from "@helpers/financialAccount";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { Menu } from "@design-system/Menu/Menu.lib";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useSettings } from "@components/settings/SettingsProvider";
import { WorkspaceSettingsPages } from "@components/settings/constants";
import { BankReconciliationRow } from "../bankReconciliationViewConfig";
import { useBankReconciliationReportDownload } from "../useBankReconciliationReportDownload";

const UNDO_FAILED_MESSAGE = "Couldn't undo reconciliation";

function formatLockDate(lockDate: string): string {
    return format(parseISO(lockDate), "MMM d, yyyy");
}

export const ActionsCell = ({
    row,
}: CellContext<ViewTableFeatures, BankReconciliationRow, any>) => {
    const financialAccount = row.original.financialAccount;
    const reconciliation = row.original.bankReconciliation;
    const reconciliationNumber = row.original.reconciliationNumber;
    const {
        bankReconciliationTransactionsUndo:
            bankReconciliationTransactionsUndoEnabled,
    } = useTypedFlags();
    const { download: downloadReport, isDownloading: isDownloadingReport } =
        useBankReconciliationReportDownload(reconciliation?.id);
    const navigate = useStateAwareNavigate();

    if (!financialAccount.entityId) {
        throw new Error("Financial account has no entity");
    }

    const reconcileDisabledReason = useViewOnlyTooltipReason(
        getReconcileDisabledReason(financialAccount) ?? "",
    );

    const [showUndoModal, setShowUndoModal] = useState(false);
    const [showAuditLogDrawer, setShowAuditLogDrawer] = useState(false);
    const queryClient = useQueryClient();
    const { error: toastError } = useToaster();
    const { openOverlay } = useOverlays();
    const { open: openSettings } = useSettings();
    const { data: lockBlockers, isLoading: isLoadingLockBlockers } =
        useUndoReconciliationLockBlockers({
            entityId: financialAccount.entityId,
            financialAccountId: financialAccount.id,
            enabled: showUndoModal,
        });
    const lockedEntities = lockBlockers?.lockedEntities ?? [];
    const hasLockBlockers = lockedEntities.length > 0;

    const handleReconcile = () => {
        openOverlay("reconcilieFinancialAccount", {
            financialAccountId: financialAccount.id,
            previousReconciliation: row.original.mostRecentReconciliation,
        });
    };
    const removalMutation = useLastBankReconciliationRemovalMutation(
        {
            entityId: financialAccount.entityId,
            financialAccountId: financialAccount.id,
        },
        () => {
            setShowUndoModal(false);
        },
        (error) => {
            if (isReconciliationPeriodLockedError(error)) {
                void queryClient.invalidateQueries({
                    queryKey: undoReconciliationLockBlockersQueryKey,
                });
                return;
            }

            if (isViewOnlyRefusalError(error)) {
                toastError(
                    VIEW_ONLY_WRITE_REFUSAL_MESSAGE,
                    VIEW_ONLY_TOAST_KEY,
                );
                return;
            }

            if (
                isAxiosError<{ message?: string }>(error) &&
                error.response?.status === 400
            ) {
                toastError(error.response.data?.message ?? UNDO_FAILED_MESSAGE);
                return;
            }

            toastError(UNDO_FAILED_MESSAGE);
        },
    );

    const getUndoQuestion = () => {
        if (hasLockBlockers) {
            return (
                <VStack gap={12}>
                    <span>
                        Undo is blocked because reconciled transactions fall in
                        locked periods. Unlock the affected entities, then try
                        again.
                    </span>
                    <ul>
                        {lockedEntities.map((lockedEntity) => (
                            <li key={lockedEntity.entityId}>
                                {lockedEntity.entityName} —{" "}
                                {pluralizeWithCount(
                                    "transaction",
                                    lockedEntity.transactionCount,
                                )}{" "}
                                (locked through{" "}
                                {formatLockDate(lockedEntity.lockDate)})
                            </li>
                        ))}
                    </ul>
                </VStack>
            );
        }

        if (bankReconciliationTransactionsUndoEnabled) {
            return "This will mark previously reconciled transactions as not reconciled and revert the changes made to transactions during this reconciliation.";
        }

        return "This will mark previously reconciled transactions as not reconciled. Edits made to transactions when doing this reconciliation won't be undone.";
    };
    const undoQuestion = getUndoQuestion();

    return (
        <ViewTableCell>
            <HStack justify="end">
                {!reconciliation && (
                    <Tooltip
                        key={reconcileDisabledReason}
                        content={reconcileDisabledReason}
                    >
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleReconcile}
                            disabled={!!reconcileDisabledReason}
                        >
                            Reconcile
                        </Button>
                    </Tooltip>
                )}
                {reconciliation && (
                    <Menu.More className={tableStyles.showOnHover}>
                        {row.original.isLastReconciliation && (
                            <Menu.Item onClick={() => setShowUndoModal(true)}>
                                Undo
                            </Menu.Item>
                        )}
                        <Menu.Item
                            onClick={() =>
                                navigate(
                                    `/reconciliation/report/${reconciliation.id}`,
                                )
                            }
                        >
                            View report
                        </Menu.Item>
                        <ExportMenuItem
                            isDownloading={isDownloadingReport}
                            onClick={downloadReport}
                        >
                            Download report
                        </ExportMenuItem>
                        <Menu.Item onClick={() => setShowAuditLogDrawer(true)}>
                            View History
                        </Menu.Item>
                    </Menu.More>
                )}
                <ConfirmationModal
                    show={showUndoModal}
                    onHide={() => setShowUndoModal(false)}
                    onReject={() => setShowUndoModal(false)}
                    title="Undo last reconciliation?"
                    question={undoQuestion}
                    yes={hasLockBlockers ? "Go to locks" : "Undo"}
                    no="Cancel"
                    confirmDisabled={isLoadingLockBlockers}
                    {...(hasLockBlockers
                        ? {
                              onConfirm: () => {
                                  setShowUndoModal(false);
                                  openSettings([WorkspaceSettingsPages.LOCKS]);
                              },
                          }
                        : {
                              onConfirmMutation: removalMutation,
                          })}
                />

                {reconciliation && (
                    <ReconciliationLogDrawer
                        open={showAuditLogDrawer}
                        onClose={() => setShowAuditLogDrawer(false)}
                        reconciliation={reconciliation}
                        financialAccount={financialAccount}
                        reconciliationNumber={reconciliationNumber}
                    />
                )}
            </HStack>
        </ViewTableCell>
    );
};
